"use client";

import React, { useState } from "react";

type TabKey =
    | "Residential"
    | "Commercial"
    | "PG/Co-living"
    | "Plot"
    | "Land"
    | "Bank Auction";

interface Props {
    tabs: { key: TabKey; label: string; icon: string; badge?: string }[];
    activeTab: TabKey;
    setActiveTab: (key: TabKey) => void;
    buyMode: "Buy" | "Rent";
    setBuyMode: (mode: "Buy" | "Rent") => void;
    selectedType: string;
    setSelectedType: (type: string) => void;
    selectedCity: string;
    setSelectedCity: (city: string) => void;
    searchText: string;
    setSearchText: (text: string) => void;
    handleSearch: (city?: string) => void;
    setShowAdvanced: (show: boolean) => void;
    cities: string[];
    currentTabTypes: string[];
}

const MobileSearchForm: React.FC<Props> = ({
    tabs,
    activeTab,
    setActiveTab,
    buyMode,
    setBuyMode,
    selectedType,
    setSelectedType,
    selectedCity,
    setSelectedCity,
    searchText,
    setSearchText,
    handleSearch,
    setShowAdvanced,
    cities,
    currentTabTypes,
}) => {
    const [openSheet, setOpenSheet] = useState<
        "mode" | "type" | "city" | null
    >(null);

    return (
        <div className="lg:hidden w-full pb-0.5 font-outfit bg-white">
            {/* 🔹 Background Hero Section Style */}
            <div className="relative pt-1 pb-0.5 px-8 max-w-[400px] mx-auto bg-white">
                {/* Architectural blueprint background placeholder - extremely subtle */}
                <div 
                    className="absolute inset-0 opacity-[0.03] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h80v80H10z' fill='none' stroke='%2305144b' stroke-width='0.5'/%3E%3Cpath d='M20 20h60v60H20z' fill='none' stroke='%2305144b' stroke-width='0.5'/%3E%3Cpath d='M0 50h100M50 0v100' stroke='%2305144b' stroke-width='0.2'/%3E%3C/svg%3E")`,
                        backgroundSize: '150px 150px'
                    }}
                />
                
                {/* 🔹 Header: Exploring Options */}
                <div className="relative z-20 flex flex-col items-center mb-1">
                    <div className="flex items-center gap-1.5 text-black text-[11px] font-medium relative">
                        <span>Exploring options in</span>
                        <div className="relative">
                            <button 
                                onClick={() => setOpenSheet(openSheet === "city" ? null : "city")}
                                className="flex items-center gap-1 font-black text-[#05144b] text-sm transition-transform active:scale-95"
                            >
                                {selectedCity}
                                <i className={`fa-solid fa-chevron-down text-[8px] mt-0.5 opacity-60 transition-transform ${openSheet === "city" ? "rotate-180" : ""}`}></i>
                            </button>

                            {/* Dropdown for City (Compact & opens below) */}
                            {openSheet === "city" && (
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-2xl shadow-[0_15px_50px_rgba(0,0,0,0.25)] border border-slate-100 z-[1001] min-w-[180px] max-h-[350px] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="p-1">
                                        {["All Cities", ...cities].map((c) => (
                                            <button
                                                key={c}
                                                onClick={() => {
                                                    setSelectedCity(c);
                                                    setOpenSheet(null);
                                                }}
                                                className={`w-full text-left px-3 py-2 rounded-lg text-[13px] font-bold transition-all mb-0.5 ${
                                                    selectedCity === c 
                                                    ? "bg-blue-50 text-[#05144b]" 
                                                    : "text-slate-600 hover:bg-slate-50"
                                                }`}
                                            >
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 🔹 Tabs Section (Chip style) */}
                <div className="relative z-10 flex overflow-x-auto gap-3 scrollbar-hide py-1 mb-1">
                    {tabs.map((tab) => {
                        const active = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-[11px] font-bold transition-all duration-300 border ${
                                    active 
                                    ? "bg-[#05144b] text-white border-[#05144b] shadow-lg shadow-blue-900/20 active:scale-95" 
                                    : "bg-slate-50 text-slate-500 border-slate-100/50 hover:bg-slate-100"
                                }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* 🔹 Main Search Container */}
                <div className="relative z-10 mt-1 bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 flex items-center h-12 p-0.5 transition-all focus-within:shadow-[0_8px_30px_rgb(0,0,0,0.1)]">
                    
                    {/* Buy/Rent Toggle Dropdown */}
                    <button 
                        onClick={() => setOpenSheet("mode")}
                        className="flex items-center gap-1 px-2.5 h-full font-black text-[#05144b] transition-colors hover:bg-slate-50 rounded-lg"
                    >
                        <span className="text-[13px]">{buyMode}</span>
                        <i className="fa-solid fa-chevron-down text-[7px] opacity-60"></i>
                    </button>

                    {/* Vertical Divider */}
                    <div className="w-[1px] h-5 bg-slate-200 mx-0.5"></div>

                    {/* Search Input Area */}
                    <div className="flex-1 flex items-center px-2 h-full">
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            placeholder="Locality Project"
                            className="w-full bg-transparent outline-none text-[13px] font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-medium"
                        />
                    </div>

                    {/* Action Button (Search) */}
                    <button 
                        onClick={() => handleSearch()}
                        className="w-9 h-9 bg-[#05144b] rounded-lg flex items-center justify-center text-white transition-all hover:bg-blue-900 active:scale-95 shadow-lg shadow-blue-900/20"
                    >
                        <i className="fa-solid fa-magnifying-glass text-xs"></i>
                    </button>
                </div>

                {/* Advanced Filters Button */}
                <div className="mt-1 flex justify-end">
                    <button 
                        onClick={() => setShowAdvanced(true)}
                        className="flex items-center gap-1 text-[9px] font-black text-[#05144b] bg-slate-50 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors border border-slate-100"
                    >
                        <i className="fa-solid fa-sliders text-[8px]"></i>
                        ADVANCED
                    </button>
                </div>
            </div>

            {/* 🔥 Bottom Sheets */}
            {openSheet === "mode" && (
                <BottomSheet onClose={() => setOpenSheet(null)}>
                    <div className="space-y-1">
                        {["Buy", "Rent"].map((m) => (
                            <Item
                                key={m}
                                label={m}
                                active={buyMode === m}
                                onClick={() => {
                                    setBuyMode(m as any);
                                    setOpenSheet(null);
                                }}
                            />
                        ))}
                    </div>
                </BottomSheet>
            )}

            {/* Type Sheet (Can be triggered if needed, though the tabs handle main categories) */}
            {openSheet === "type" && (
                <BottomSheet onClose={() => setOpenSheet(null)}>
                    <div className="grid grid-cols-2 gap-2">
                        {currentTabTypes.map((t) => (
                            <Item
                                key={t}
                                label={t}
                                active={selectedType === t}
                                onClick={() => {
                                    setSelectedType(t);
                                    setOpenSheet(null);
                                }}
                            />
                        ))}
                    </div>
                </BottomSheet>
            )}

        </div>
    );
};

export default MobileSearchForm;

/* 🔹 Bottom Sheet Component */
const BottomSheet = ({
    children,
    onClose,
}: {
    children: React.ReactNode;
    onClose: () => void;
}) => {
    return (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-[2px] flex items-end animate-in fade-in duration-300">
            <button className="absolute inset-0 w-full h-full cursor-default" onClick={onClose}></button>
            <div className="relative bg-white w-full rounded-t-[32px] p-4 pb-8 max-h-[75vh] flex flex-col shadow-[0_-10px_40px_rgba(0,0,0,0.15)] animate-in slide-in-from-bottom duration-500">
                <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4 shrink-0"></div>
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {children}
                </div>
                <button
                    onClick={onClose}
                    className="mt-4 w-full py-3 bg-slate-50 text-slate-500 font-bold text-sm rounded-2xl hover:bg-slate-100 transition-colors shrink-0"
                >
                    Close
                </button>
            </div>
        </div>
    );
};

/* 🔹 Item Component */
const Item = ({
    label,
    active,
    onClick,
}: {
    label: string;
    active?: boolean;
    onClick: () => void;
}) => {
    return (
        <button
            onClick={onClick}
            className={`w-full text-left p-3 rounded-xl mb-1 text-[14px] font-bold transition-all ${
                active 
                ? "bg-blue-50 text-[#05144b] border border-blue-100/50 shadow-sm" 
                : "bg-white text-slate-700 hover:bg-slate-50 border border-transparent"
            }`}
        >
            {label}
        </button>
    );
};